import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersApi, UserFilterParams } from '@/api/users'
import type { User } from '@/api/users'
import { customerKeys } from './useCustomers'

// ============================================
// Query Keys
// ============================================
export const userKeys = {
	all: ['users'] as const,
	lists: () => [...userKeys.all, 'list'] as const,
	list: (params?: { page?: number; page_size?: number } & UserFilterParams) =>
		[...userKeys.lists(), params] as const,
	details: () => [...userKeys.all, 'detail'] as const,
	detail: (id: number) => [...userKeys.details(), id] as const,
}

// ============================================
// 获取用户列表 Hook
// ============================================
interface UseUsersParams extends UserFilterParams {
	page?: number
	page_size?: number
	enabled?: boolean
}

export function useUsers(params?: UseUsersParams) {
	return useQuery({
		queryKey: userKeys.list(params),
		queryFn: () => usersApi.list(params),
		enabled: params?.enabled !== false,
	})
}

// ============================================
// 获取用户详情 Hook
// ============================================
export function useUserDetail(id: number | null, enabled = true) {
	return useQuery({
		queryKey: userKeys.detail(id || 0),
		queryFn: () => usersApi.get(id || 0),
		enabled: enabled && !!id,
	})
}

// ============================================
// 用于部门管理的简化 Hook
// ============================================
export function useUserList() {
	const queryClient = useQueryClient()

	const refetch = () => {
		queryClient.invalidateQueries({ queryKey: userKeys.lists() })
	}

	return { refetch }
}

// 重新导出 User 类型
export type { User }

// ============================================
// 员工整批转交 Hook
// ============================================
export function useTransferUser() {
	const queryClient = useQueryClient()

	return useMutation({
		mutationFn: ({ from_user_id, to_user_id }: { from_user_id: number; to_user_id: number }) =>
			usersApi.transfer(from_user_id, to_user_id),
		onSuccess: () => {
			// 转交影响用户数据域与客户列表，两者缓存都要失效
			queryClient.invalidateQueries({ queryKey: userKeys.all })
			queryClient.invalidateQueries({ queryKey: customerKeys.all })
		},
	})
}
